import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import type { FlattenedFunnelConfig } from '../../config/flattened/types';
import {
  getBranchForPrimaryGoal,
  getBranchStep,
  isStepSatisfied,
  resolveNextStepId,
} from './branch';
import { ENTRY_STEP_ID, RECOMMENDATIONS } from './constants';

/**
 * Returns true only if `answers` represents a funnel run the user actually
 * finished: the walk from the branch entry reaches the terminal step, with
 * every step along the way answered unless it is skippable.
 *
 * Both halves of that rule are load-bearing. Requiring an answer at every step
 * rejects anyone who skipped an optional one; following transitions without
 * checking answers accepts `primary_goal` on its own, since some branches are
 * entirely static transitions.
 *
 * Distinct from the store's `shouldSkipRecommendations`, which only gates
 * mid-funnel prefetching.
 */
export function isFunnelComplete(config: FlattenedFunnelConfig, answers: AnswerState): boolean {
  const primaryGoal = answers[ENTRY_STEP_ID];
  if (typeof primaryGoal !== 'string') return false;

  const branchId = getBranchForPrimaryGoal(config, primaryGoal);
  if (!branchId) return false;

  const branch = config.branches[branchId];
  if (!branch) return false;

  let currentId: string = branch.entry;
  const visited = new Set<string>();

  while (!visited.has(currentId)) {
    visited.add(currentId);

    const branchStep = getBranchStep(config, branchId, currentId);
    if (!branchStep) return false;

    // Checked before resolving the transition: reaching the terminal step is
    // not the same as having answered it.
    if (!isStepSatisfied(branchStep, answers)) return false;

    const answer = answers[currentId] as string | undefined;

    const next = resolveNextStepId(config, branchId, currentId, answer);
    if (next === RECOMMENDATIONS) return true;
    if (!next) return false;
    currentId = next;
  }

  // Revisiting a step means the config cycles; treat as not complete.
  return false;
}
